import React, { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { loginWithPassword } from '../services/api';
import CursorField from '../components/CursorField';

/* ── Brand palette (login is intentionally theme-independent so it always
      carries the green TSA identity, light or dark mode). ── */
const GREEN_DEEP = '#0e5b46';
const GREEN_MID = '#146b54';
const GREEN_SOFT = '#1a7f64';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* Soft ambient motion for the glow patches — a slow float + gentle pulse.
   Per-blob direction/scale come from CSS custom properties set inline so each
   patch moves differently. */
const drift = keyframes`
  0%   { transform: translate(0, 0) scale(1); opacity: var(--op-min, 0.4); }
  50%  { transform: translate(var(--dx, 36px), var(--dy, -28px)) scale(var(--sc, 1.18)); opacity: var(--op-max, 0.8); }
  100% { transform: translate(0, 0) scale(1); opacity: var(--op-min, 0.4); }
`;

const Page = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  /* [APP-FIT 2026-10-26, user] Scrolls on EVERY screen when the logo + card (about 773 px) do not fit. It was overflow
     hidden, so on a short laptop window (1366 x 768 = 613 px tall in Edge) the Login button and the footer were cut off
     and could not be reached; the app zoom (utils/appZoom) now also makes it fit such a laptop, and this is the safety
     net for anything shorter. It still sits in the middle when it fits; the glow patches and the dot field are pinned
     to the screen (fixed) so they never add scroll room or scroll away. Phones had this since 2026-10-22. */
  overflow-x: hidden;
  overflow-y: auto;
  & > canvas { position: fixed; }
  background:
    radial-gradient(1200px 600px at 15% -10%, ${GREEN_SOFT} 0%, transparent 55%),
    radial-gradient(900px 500px at 110% 110%, ${GREEN_SOFT} 0%, transparent 50%),
    linear-gradient(135deg, ${GREEN_DEEP} 0%, ${GREEN_MID} 55%, ${GREEN_DEEP} 100%);

  /* [PHONE-LAYOUT 2026-10-22] Phones: a tighter edge. */
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

/* Soft floating light patches that slowly drift + pulse for an ambient,
   premium feel. Big + heavily blurred so they read as moving light, not circles. */
const Blob = styled.div<{ $size: number; $top: string; $left: string; $delay: string; $dur: string }>`
  position: fixed; /* [APP-FIT 2026-10-26] pinned to the screen on every screen, see Page (the same place: Page fills it) */
  top: ${(p) => p.$top};
  left: ${(p) => p.$left};
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0) 70%);
  filter: blur(44px);
  pointer-events: none;
  animation: ${drift} ${(p) => p.$dur} ease-in-out infinite;
  animation-delay: ${(p) => p.$delay};
  will-change: transform, opacity;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 540px;
  padding: 58px 56px 44px;
  border-radius: 28px;
  /* [RESPONSIVE 2026-06-23] On phones the fixed 56px side padding ate most of the
     card's ~352px width, cramping the title and the Google button. Shrink it. */
  @media (max-width: 480px) {
    padding: 40px 24px 32px;
    border-radius: 22px;
  }
  background: rgba(255, 255, 255, 0.97);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow:
    0 30px 70px rgba(0, 0, 0, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  text-align: center;
`;

/* Logo sits on the green page (not the white card) — the TSA mark is a white
   wordmark, so it only reads on the dark brand background. */
const Stack = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 540px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 26px;
  animation: ${fadeUp} 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

const BrandLogo = styled.img`
  height: 78px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 6px 18px rgba(0, 0, 0, 0.35));
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 29px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: #0f172a;
`;

const Subtitle = styled.p`
  margin: 0 0 34px;
  font-size: 16.5px;
  line-height: 1.5;
  color: #64748b;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  padding: 11px 14px;
  border-radius: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13px;
  font-weight: 500;
  text-align: left;

  i {
    font-size: 14px;
    flex-shrink: 0;
  }
`;

// [ACCESS-KILLSWITCH 2026-07-07] Neutral (non-error) notice shown when the user was
// signed out because an admin changed their access — explains the re-login instead of a
// bare login screen. Blue/info tone so it doesn't read as a failure.
const NoticeBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  padding: 11px 14px;
  border-radius: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 500;
  text-align: left;

  i {
    font-size: 14px;
    flex-shrink: 0;
  }
`;

const GoogleButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 13px;
  width: 100%;
  height: 56px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #dadce0;
  color: #3c4043;
  font-size: 17px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: background 180ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1), border-color 180ms cubic-bezier(0.4, 0, 0.2, 1), transform 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: #f8faff;
    border-color: #c6cdd6;
    box-shadow: 0 3px 10px rgba(60, 64, 67, 0.16);
  }

  &:active {
    transform: translateY(1px);
  }

  svg {
    width: 21px;
    height: 21px;
    flex-shrink: 0;
  }
`;

/* [PM-LOGIN 2026-08-11] Divider between the two sign-in routes. A labelled rule rather than a
   bare gap, so the form below reads as an alternative to Google and not as a second required step. */
const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 22px 0 20px;
  color: #94a3b8;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e6eaf0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
`;

const Input = styled.input`
  height: 48px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid #dadce0;
  background: #fff;
  color: #0f172a;
  font-size: 15px;
  font-weight: 500;
  outline: none;
  transition: border-color 180ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &::placeholder { color: #b6c0cd; font-weight: 400; }

  &:focus {
    border-color: ${GREEN_SOFT};
    box-shadow: 0 0 0 3px rgba(26, 127, 100, 0.16);
  }

  &:disabled { background: #f6f8fa; color: #94a3b8; }
`;

/* [PW-EYE 2026-09-30, user] "in the password row the eye button is not visible" - there was none.
   The show/hide button sits INSIDE the Password box at its right end; the box keeps room for it so
   typed text never runs underneath. Edge draws its own reveal eye in a password field and ours
   replaces it everywhere, so Edge's is switched off - never two eyes. */
const PasswordWrap = styled.div`
  position: relative;
  display: flex;

  & > input {
    flex: 1;
    min-width: 0;
    padding-right: 48px;
  }
  & > input::-ms-reveal,
  & > input::-ms-clear { display: none; }
`;

const EyeButton = styled.button`
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 9px;
  background: transparent;
  color: #94a3b8;
  font-size: 15px;
  transition: color 160ms cubic-bezier(0.4, 0, 0.2, 1), background 160ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) { color: #475569; background: #f1f5f9; }
  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

const SubmitButton = styled.button`
  height: 52px;
  margin-top: 4px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(180deg, ${GREEN_SOFT} 0%, ${GREEN_MID} 100%);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.2px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(20, 107, 84, 0.32);
  transition: filter 180ms cubic-bezier(0.4, 0, 0.2, 1), transform 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) { filter: brightness(1.07); box-shadow: 0 8px 22px rgba(20, 107, 84, 0.4); }
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { opacity: 0.62; cursor: not-allowed; box-shadow: none; }
`;

const Footer = styled.div`
  margin-top: 26px;
  padding-top: 18px;
  border-top: 1px solid #eef1f5;
  font-size: 12px;
  line-height: 1.5;
  color: #94a3b8;
`;

/* Official multi-colour Google "G" mark. */
const GoogleIcon: React.FC = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

const ERROR_MESSAGES: Record<string, string> = {
  domain_not_allowed:
    'That email domain isn’t authorized for this dashboard. Use your company Google account.',
  deactivated: 'Your account has been deactivated. Please contact an administrator.',
  unverified_email: 'Your Google email address isn’t verified.',
  no_token: 'Sign-in could not be completed. Please try again.',
  missing_code: 'Sign-in was interrupted. Please try again.',
  auth_failed: 'Sign-in failed. Please try again.',
};

const LoginPage: React.FC = () => {
  const { loginUrl, refresh } = useAuth();

  // [PM-LOGIN 2026-08-11] Username/password sign-in, beside Google.
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); // [PW-EYE 2026-09-30] the eye button
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setFormError(null);
    setSubmitting(true);
    try {
      await loginWithPassword(username, password);
      // The cookie is set; refresh() re-reads /auth/me and the Gate swaps this screen for the app.
      // No local navigation — the routing decision belongs to the Gate, which is the only thing
      // that knows what this account may see.
      await refresh();
    } catch (err: any) {
      setFormError(err?.message || 'Sign-in failed. Please try again.');
      setPassword(''); // never leave a rejected password sitting in the field
      setShowPassword(false); // [PW-EYE 2026-09-30] and the next one starts hidden again
      setSubmitting(false);
    }
  };

  const errorMessage = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('login_error');
    if (!code) return null;
    return ERROR_MESSAGES[code] || 'Sign-in failed. Please try again.';
  }, []);

  // [ACCESS-KILLSWITCH 2026-07-07] Shown once when the user was auto-signed-out because
  // their access changed (flag set by the api 401 interceptor). Read-and-clear so it
  // doesn't linger on a later, ordinary visit to the login screen.
  const accessNotice = useMemo(() => {
    try {
      if (sessionStorage.getItem('dt_access_notice')) {
        sessionStorage.removeItem('dt_access_notice');
        return 'Your access was updated. Please sign in again to continue.';
      }
    } catch { /* ignore */ }
    return null;
  }, []);

  return (
    <Page>
      <Blob
        $size={440} $top="-140px" $left="-110px" $delay="0s" $dur="17s"
        style={{ ['--dx' as any]: '55px', ['--dy' as any]: '40px', ['--sc' as any]: 1.16, ['--op-min' as any]: 0.4, ['--op-max' as any]: 0.85 }}
      />
      <Blob
        $size={340} $top="58%" $left="76%" $delay="-5s" $dur="21s"
        style={{ ['--dx' as any]: '-45px', ['--dy' as any]: '-32px', ['--sc' as any]: 1.22, ['--op-min' as any]: 0.35, ['--op-max' as any]: 0.8 }}
      />
      <Blob
        $size={240} $top="80%" $left="6%" $delay="-10s" $dur="19s"
        style={{ ['--dx' as any]: '34px', ['--dy' as any]: '-42px', ['--sc' as any]: 1.12, ['--op-min' as any]: 0.3, ['--op-max' as any]: 0.7 }}
      />
      <Blob
        $size={200} $top="4%" $left="68%" $delay="-14s" $dur="24s"
        style={{ ['--dx' as any]: '-28px', ['--dy' as any]: '38px', ['--sc' as any]: 1.2, ['--op-min' as any]: 0.3, ['--op-max' as any]: 0.72 }}
      />

      {/* Cursor-reactive dot field (drift + parallax + soft repel). */}
      <CursorField />

      <Stack>
        <BrandLogo src="https://thescale.agency/images/tsa/tsa-logo.svg" alt="The Scale Agency" />

        <Card>
          <Title>Welcome back</Title>
          <Subtitle>Sign in to access the Scale Agency Dashboard</Subtitle>

          {accessNotice && !errorMessage && (
            <NoticeBanner>
              <i className="fas fa-circle-info" />
              <span>{accessNotice}</span>
            </NoticeBanner>
          )}

          {(errorMessage || formError) && (
            <ErrorBanner>
              <i className="fas fa-circle-exclamation" />
              <span>{formError || errorMessage}</span>
            </ErrorBanner>
          )}

          <GoogleButton href={loginUrl}>
            <GoogleIcon />
            Continue with Google
          </GoogleButton>

          {/* [PM-LOGIN 2026-08-11] Second sign-in route, for accounts issued a username and
              password rather than a Google identity. A real <form> so Enter submits and browser
              password managers recognise it.
              [DASH-LOGIN 2026-09-02] It now also takes a WORK EMAIL — anyone whose Google
              sign-in is unavailable enters their email plus the dashboard password and lands on
              their own account, so the field is labelled for both. */}
          <Divider>or</Divider>

          <Form onSubmit={onSubmit}>
            <Field>
              Email or username
              <Input
                type="text"
                name="username"
                autoComplete="username"
                placeholder="Enter your email or username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={submitting}
              />
            </Field>
            <Field>
              Password
              {/* [PW-EYE 2026-09-30, user] Show / hide toggle inside the box (see PasswordWrap). The
                  input stays FIRST inside this label, so the label still points at it. mouseDown is
                  cancelled so the typing cursor stays in the box when the eye is clicked. */}
              <PasswordWrap>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
                <EyeButton
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  onMouseDown={(e) => e.preventDefault()}
                  disabled={submitting}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'} />
                </EyeButton>
              </PasswordWrap>
            </Field>
            <SubmitButton type="submit" disabled={submitting || !username.trim() || !password}>
              {submitting ? 'Signing in…' : 'Login'}
            </SubmitButton>
          </Form>

          <Footer>
            Google sign-in is for authorized company accounts only.
            <br />
            Access is granted by your administrator after first sign-in.
          </Footer>
        </Card>
      </Stack>
    </Page>
  );
};

export default LoginPage;
